/**
 * CuentaNode — nodo recursivo del árbol del catálogo.
 *
 * El estado `expanded` ya NO vive aquí — vive en ListCatalogo
 * en un Set<id> que sobrevive al refetch. Esto evita que los acordeones
 * se colapsen al agregar/editar una cuenta.
 *
 * Props:
 *   cuenta        → objeto CuentaContable con subcuentas anidadas
 *   depth         → nivel de indentación visual (0-based)
 *   expandedIds   → Set<number> de IDs actualmente expandidos
 *   onToggle      → fn(id) → toggle en el Set del padre
 *   onAgregar     → fn(cuenta)
 *   onEditar      → fn(cuenta)
 *   onDesactivar  → fn(id, nombre)
 *   onActivar     → fn(id)
 */
import { useTranslation } from "react-i18next";

const TIPO_COLOR = {
  ACTIVO:     { text: "#60a5fa" },
  PASIVO:     { text: "#f87171" },
  PATRIMONIO: { text: "#a78bfa" },
  INGRESO:    { text: "#4ade80" },
  GASTO:      { text: "#fbbf24" },
};

export function CuentaNode({
  cuenta,
  depth = 0,
  expandedIds,
  onToggle,
  onAgregar,
  onEditar,
  onDesactivar,
  onActivar,
}) {
  const { t } = useTranslation();
  const expanded   = expandedIds.has(cuenta.id);
  const tieneHijos = cuenta.subcuentas?.length > 0 || cuenta.tiene_hijos;
  const color      = TIPO_COLOR[cuenta.tipo] || TIPO_COLOR.ACTIVO;
  const indent     = depth * 20;

  const bgRow      = depth === 0 ? "var(--hover-bg)" : "transparent";
  const bgRowHover = "var(--hover-bg-strong)";

  return (
    <div>
      <div
        style={{
          display:      "flex",
          alignItems:   "center",
          gap:          8,
          padding:      `7px 14px 7px ${14 + indent}px`,
          borderBottom: "1px solid var(--border-card)",
          background:   bgRow,
          opacity:      cuenta.activa ? 1 : 0.5,
          transition:   "background 0.12s",
          cursor:       "default",
          minWidth:     480,
        }}
        onMouseOver={(e) => { e.currentTarget.style.background = bgRowHover; }}
        onMouseOut={(e)  => { e.currentTarget.style.background = bgRow; }}
      >
        {/* Toggle */}
        <button
          type="button"
          onClick={() => onToggle(cuenta.id)}
          disabled={!tieneHijos}
          style={{
            background: "none", border: "none",
            cursor: tieneHijos ? "pointer" : "default",
            color: tieneHijos ? "#f97316" : "transparent",
            width: 18, flexShrink: 0, fontSize: "0.68rem", padding: 0,
          }}
        >
          <i className={`fa-solid fa-chevron-${expanded ? "down" : "right"}`} />
        </button>

        {/* Dot indicador */}
        <div style={{
          width: 6, height: 6, borderRadius: "50%",
          background: color.text, flexShrink: 0,
          opacity: cuenta.is_detalle ? 1 : 0.4,
        }} />

        {/* Código */}
        <span style={{
          fontFamily: "'JetBrains Mono', 'Courier New', monospace",
          fontSize: "0.76rem", color: "var(--text-muted)",
          minWidth: 112, flexShrink: 0,
          textDecoration: cuenta.activa ? "none" : "line-through",
        }}>
          {cuenta.codigo}
        </span>

        {/* Nombre */}
        <span style={{
          flex: 1,
          color: depth === 0 ? "var(--text-primary)" : "var(--text-secondary)",
          fontSize: depth === 0 ? "0.87rem" : "0.83rem",
          fontWeight: depth === 0 ? 600 : 400,
        }}>
          {cuenta.nombre}
        </span>

        {/* Badges */}
        <div style={{ display: "flex", gap: 5, alignItems: "center", flexShrink: 0 }}>
          <span style={{
            fontSize: "0.63rem", fontWeight: 700,
            padding: "2px 7px", borderRadius: 20,
            background: `${color.text}18`,
            border: `1px solid ${color.text}40`,
            color: color.text,
          }}>
            {cuenta.tipo}
          </span>

          {cuenta.is_detalle && (
            <span style={{
              fontSize: "0.63rem", fontWeight: 700,
              padding: "2px 7px", borderRadius: 20,
              background: "rgba(249,115,22,0.15)",
              border: "1px solid rgba(249,115,22,0.35)",
              color: "#f97316",
            }}>
              {t("catalogo.detalleTag")}
            </span>
          )}

          {cuenta.clasificacion && (
            <span title={cuenta.clasificacion} style={{
              fontSize: "0.63rem", fontWeight: 700,
              padding: "2px 7px", borderRadius: 20,
              background: "rgba(167,139,250,0.15)",
              border: "1px solid rgba(167,139,250,0.35)",
              color: "#a78bfa",
              maxWidth: 160, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap",
            }}>
              <i className="fa-solid fa-tag" style={{ marginRight: 4, fontSize: "0.58rem" }} />
              {cuenta.clasificacion}
            </span>
          )}

          {cuenta.es_costo_venta && (
            <span style={{
              fontSize: "0.63rem", fontWeight: 700,
              padding: "2px 7px", borderRadius: 20,
              background: "rgba(251,191,36,0.15)",
              border: "1px solid rgba(251,191,36,0.35)",
              color: "#fbbf24",
            }}>
              {t("catalogo.costoVentaTag")}
            </span>
          )}

          {!cuenta.activa && (
            <span style={{
              fontSize: "0.63rem", padding: "2px 7px", borderRadius: 20,
              background: "rgba(107,114,128,0.15)", color: "var(--text-muted)",
              border: "1px solid rgba(107,114,128,0.3)",
            }}>
              {t("catalogo.inactivaTag")}
            </span>
          )}

          <span style={{ fontSize: "0.63rem", color: "var(--border-card)", minWidth: 12, textAlign: "center" }}>
            {cuenta.naturaleza === "DEUDORA" ? "D" : "H"}
          </span>
        </div>

        {/* Acciones */}
        <div style={{ display: "flex", gap: 2, flexShrink: 0 }}>
          {!cuenta.is_detalle && cuenta.nivel < 4 && cuenta.activa && (
            <ActionBtn icon="fa-plus"     title={t("catalogo.agregarSubcuenta")} hoverColor="#f97316" onClick={() => onAgregar(cuenta)} />
          )}
          {cuenta.nivel > 1 && (
            <ActionBtn icon="fa-pen"      title={t("common.editar")}             hoverColor="#60a5fa" onClick={() => onEditar(cuenta)} />
          )}
          {cuenta.nivel > 1 && (
            cuenta.activa
              ? <ActionBtn icon="fa-eye-slash" title={t("catalogo.desactivar")} hoverColor="#f87171" onClick={() => onDesactivar(cuenta.id, cuenta.nombre)} />
              : <ActionBtn icon="fa-eye"       title={t("catalogo.reactivar")}  hoverColor="#4ade80" onClick={() => onActivar(cuenta.id)} />
          )}
        </div>
      </div>

      {/* Subcuentas recursivas */}
      {expanded && cuenta.subcuentas?.map((sub) => (
        <CuentaNode
          key={sub.id}
          cuenta={sub}
          depth={depth + 1}
          expandedIds={expandedIds}
          onToggle={onToggle}
          onAgregar={onAgregar}
          onEditar={onEditar}
          onDesactivar={onDesactivar}
          onActivar={onActivar}
        />
      ))}
    </div>
  );
}

function ActionBtn({ icon, title, hoverColor, onClick }) {
  return (
    <button
      type="button"
      title={title}
      onClick={onClick}
      style={{
        background: "none", border: "none", cursor: "pointer",
        color: "var(--text-muted)", padding: "3px 5px", borderRadius: 5,
        fontSize: "0.7rem", transition: "color 0.15s, background 0.15s",
      }}
      onMouseOver={(e) => {
        e.currentTarget.style.color = hoverColor;
        e.currentTarget.style.background = `${hoverColor}18`;
      }}
      onMouseOut={(e) => {
        e.currentTarget.style.color = "var(--text-muted)";
        e.currentTarget.style.background = "none";
      }}
    >
      <i className={`fa-solid ${icon}`} />
    </button>
  );
}
